import type {AriaListBoxProps} from '@react-types/listbox';
import type {DOMAttributes, KeyboardDelegate} from '@react-types/shared';
import type {RefObject} from 'react';
import {useFocusWithin} from '../interactions';
import {filterDOMProps, mergeProps, useId} from '../utils';
import {useSelectableList} from '../selection';
import type {ListState} from '../../@react-stately/list';
import {useLabel} from '../label';

import {listData} from './utils';

export interface ListBoxAria {
  /** Props for the listbox element. */
  listBoxProps: DOMAttributes;
  /** Props for the listbox's visual label element (if any). */
  labelProps: DOMAttributes;
}

export interface AriaListBoxOptions<T>
  extends Omit<AriaListBoxProps<T>, 'children'> {
  /** Whether the listbox uses virtual scrolling. */
  isVirtualized?: boolean;

  /**
   * An optional keyboard delegate implementation for type to select,
   * to override the default.
   */
  keyboardDelegate?: KeyboardDelegate;

  /**
   * Whether the listbox items should use virtual focus instead of being focused directly.
   */
  shouldUseVirtualFocus?: boolean;

  /** Whether selection should occur on press up instead of press down. */
  shouldSelectOnPressUp?: boolean;

  /** Whether options should be focused when the user hovers over them. */
  shouldFocusOnHover?: boolean;

  /**
   * The behavior of links in the collection.
   * - 'action': link behaves like onAction.
   * - 'selection': link follows selection interactions (e.g. if URL drives selection).
   * - 'override': links override all other interactions (link items are not selectable).
   * @default 'override'
   */
  linkBehavior?: 'action' | 'selection' | 'override';
}

/**
 * Provides the behavior and accessibility implementation for a listbox component.
 * A listbox displays a list of options and allows a user to select one or more of them.
 * @param props - Props for the listbox.
 * @param state - State for the listbox, as returned by `useListState`.
 */
export function useListBox<T>(
  props: AriaListBoxOptions<T>,
  state: ListState<T>,
  ref: RefObject<HTMLElement>,
): ListBoxAria {
  const domProps = filterDOMProps(props, {labelable: true});
  // Use props instead of state here. We don't want this to change due to long press.
  const selectionBehavior = props.selectionBehavior || 'toggle';
  let linkBehavior =
    props.linkBehavior ||
    (selectionBehavior === 'replace' ? 'action' : 'override');
  if (selectionBehavior === 'toggle' && linkBehavior === 'action') {
    // linkBehavior="action" does not work with selectionBehavior="toggle" because there is no way
    // to initiate selection (checkboxes are not allowed inside a listbox). Link items will not be
    // selectable in this configuration.
    linkBehavior = 'override';
  }

  const {listProps} = useSelectableList({
    ...props,
    ref,
    selectionManager: state.selectionManager,
    collection: state.collection,
    disabledKeys: state.disabledKeys,
    linkBehavior,
  });

  const {focusWithinProps} = useFocusWithin({
    onFocusWithin: props.onFocus,
    onBlurWithin: props.onBlur,
    onFocusWithinChange: props.onFocusChange,
  });

  // Share list id and some props with child options.
  const id = useId(props.id);
  listData.set(state, {
    id,
    shouldUseVirtualFocus: props.shouldUseVirtualFocus,
    shouldSelectOnPressUp: props.shouldSelectOnPressUp,
    shouldFocusOnHover: props.shouldFocusOnHover,
    isVirtualized: props.isVirtualized,
    onAction: props.onAction,
    linkBehavior,
  });

  const {labelProps, fieldProps} = useLabel({
    ...props,
    id,
    // listbox is not an HTML input element so it
    // shouldn't be labeled by a <label> element.
    labelElementType: 'span',
  });

  return {
    labelProps,
    listBoxProps: mergeProps(
      domProps,
      focusWithinProps,
      state.selectionManager.selectionMode === 'multiple'
        ? {
            'aria-multiselectable': 'true',
          }
        : {},
      {
        role: 'listbox',
        ...mergeProps(fieldProps, listProps),
      },
    ),
  };
}
